import {
  EnvironmentStatus,
  type Environment as ProtoEnvironment,
  type Task as ProtoTask,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import { EnvironmentService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/environment_connect";
import type { JsonValue } from "@bufbuild/protobuf";
import type { CallOptions } from "@connectrpc/connect";
import type { AdminSearchWorkspace } from "components/Admin/types";
import { ADMIN_ENVIRONMENT_DEFAULT_PAGE_SIZE } from "constants/adminEnvironments";
import { getInstanceTypeArchitectures } from "utils/architecture";
import type { Environment as UIEnvironment } from "components/Environment/utils/types";
import { VerbBuildStatus } from "models/Workspace.model";
import { createConnectClient } from "../../connect";
import { mapEnvironment } from "./mappers";

const environmentClient = createConnectClient(EnvironmentService);
const environmentBuildTaskName = "environment-build";
const containerCloudWorkspaceGroupId = "akash-brev-wg";

function getLatestEnvironmentBuildTask(
  tasks: ProtoTask[],
): ProtoTask | undefined {
  return tasks
    .filter((task) => task.name === environmentBuildTaskName)
    .sort((left, right) => {
      const leftTime = left.createTime?.toDate().getTime() ?? 0;
      const rightTime = right.createTime?.toDate().getTime() ?? 0;
      return rightTime - leftTime;
    })[0];
}

function mapVerbBuildStatusFromTasks(tasks: ProtoTask[]): VerbBuildStatus {
  const latestTask = getLatestEnvironmentBuildTask(tasks);
  if (latestTask?.status === "running") {
    return VerbBuildStatus.Building;
  }
  if (
    tasks.some(
      (task) =>
        task.name === environmentBuildTaskName && task.status === "waiting",
    )
  ) {
    return VerbBuildStatus.Pending;
  }
  if (latestTask?.status === "succeeded") {
    return VerbBuildStatus.Completed;
  }
  if (latestTask?.status === "failed") {
    return VerbBuildStatus.CreateFailed;
  }
  return VerbBuildStatus.Unset;
}

function mapContainerBuildStatus(
  environment: ProtoEnvironment,
): VerbBuildStatus {
  switch (environment.instance?.status?.lifecycleStatus) {
    case "pending":
      return VerbBuildStatus.Building;
    case "running":
      return VerbBuildStatus.Completed;
    case "failed":
      return VerbBuildStatus.CreateFailed;
    default:
      return environment.instance?.status
        ? VerbBuildStatus.Pending
        : VerbBuildStatus.Unset;
  }
}

function mapVerbBuildStatus(environment: ProtoEnvironment): VerbBuildStatus {
  if (environment.labels?.workspaceGroupId === containerCloudWorkspaceGroupId) {
    return mapContainerBuildStatus(environment);
  }
  return mapVerbBuildStatusFromTasks(environment.tasks ?? []);
}

function mapEnvironmentStatus(status: EnvironmentStatus): string {
  switch (status) {
    case EnvironmentStatus.PENDING:
    case EnvironmentStatus.PREPARING:
    case EnvironmentStatus.PROVISIONING:
    case EnvironmentStatus.BUILDING:
      return "DEPLOYING";
    case EnvironmentStatus.RUNNING:
      return "RUNNING";
    case EnvironmentStatus.STOPPING:
      return "STOPPING";
    case EnvironmentStatus.STOPPED:
      return "STOPPED";
    case EnvironmentStatus.STARTING:
      return "STARTING";
    case EnvironmentStatus.TERMINATING:
      return "DELETING";
    case EnvironmentStatus.TERMINATED:
      return "STOPPED";
    case EnvironmentStatus.PREPARING_FAILED:
    case EnvironmentStatus.PROVISIONING_FAILED:
    case EnvironmentStatus.BUILDING_FAILED:
    case EnvironmentStatus.INSTANCE_LOST:
    case EnvironmentStatus.STOPPING_FAILED:
    case EnvironmentStatus.STARTING_FAILED:
    case EnvironmentStatus.TERMINATING_FAILED:
      return "FAILURE";
    default:
      return "DEPLOYING";
  }
}

function mapAdminSearchEnvironment(
  environment: ProtoEnvironment,
): AdminSearchWorkspace {
  const hasVMBuild = Boolean(environment.build?.vm);
  const hasContainerBuild = Boolean(environment.build?.container);
  const hasVerbBuild = Boolean(environment.build?.verb);
  return {
    id: environment.environmentId,
    name: environment.name,
    instanceType: environment.instanceType?.type,
    architectures: getInstanceTypeArchitectures(environment.instanceType),
    baseImage: environment.build?.container?.baseImage,
    vmOnlyMode: hasVMBuild && !hasContainerBuild && !hasVerbBuild,
    status: mapEnvironmentStatus(environment.status),
    verbBuildStatus: mapVerbBuildStatus(environment),
    instanceStatus:
      environment.instance?.status?.lifecycleStatus ||
      environment.instance?.status?.objectStatus,
    environment: { status: environment.status },
  };
}

export interface AdminEnvironmentDetail {
  environment: UIEnvironment;
  rawEnvironment: JsonValue;
}

export interface AdminEnvironmentListFilters {
  userId?: string;
  organizationId?: string;
  environmentId?: string;
  launchableId?: string;
}

export interface AdminEnvironmentListRequest {
  pageSize?: number;
  pageToken?: string;
  filters?: AdminEnvironmentListFilters;
}

export interface AdminEnvironmentListPage {
  environments: UIEnvironment[];
  nextPageToken: string;
}

export type AdminEnvironmentNames = Record<string, string>;

const adminEnvironmentAttachedDataOptions = {
  instance: true,
  instanceType: true,
  tasks: true,
};

export async function deleteEnvironment(
  environmentId: string,
  options?: CallOptions,
): Promise<void> {
  await environmentClient.deleteEnvironment({ environmentId }, options);
}

async function getAdminEnvironmentProto(
  environmentId: string,
  options?: CallOptions,
) {
  const response = await environmentClient.getEnvironment(
    {
      environmentId,
      attachedDataOptions: adminEnvironmentAttachedDataOptions,
    },
    options,
  );
  if (!response.environment) {
    throw new Error("environment was not returned");
  }
  return response.environment;
}

export async function getAdminEnvironmentDetail(
  environmentId: string,
  options?: CallOptions,
): Promise<AdminEnvironmentDetail> {
  const environment = await getAdminEnvironmentProto(environmentId, options);
  return {
    environment: mapEnvironment(environment),
    rawEnvironment: environment.toJson(),
  };
}

export async function listAdminEnvironments(
  request: AdminEnvironmentListRequest = {},
  options?: CallOptions,
): Promise<AdminEnvironmentListPage> {
  const filters = request.filters ?? {};
  const hasAllLabels = Object.fromEntries(
    [
      ["userId", filters.userId],
      ["organizationId", filters.organizationId],
      ["launchableId", filters.launchableId],
    ].filter((entry): entry is [string, string] => Boolean(entry[1])),
  );
  const response = await environmentClient.listEnvironment(
    {
      pageParams: {
        pageSize: request.pageSize ?? ADMIN_ENVIRONMENT_DEFAULT_PAGE_SIZE,
        pageToken: request.pageToken ?? "",
      },
      options: {
        hasAllLabels,
        orderOptions: { orderBy: "create_time", ordering: "desc" },
        includeDeleted: false,
        idFilter: filters.environmentId ? [filters.environmentId] : [],
        attachedDataOptions: {
          instance: true,
          instanceType: true,
        },
      },
    },
    options,
  );

  return {
    environments: response.items.map(mapEnvironment),
    nextPageToken: response.nextPageToken,
  };
}

export async function getAdminEnvironmentNames(
  environmentIds: string[],
  options?: CallOptions,
): Promise<AdminEnvironmentNames> {
  const ids = [...new Set(environmentIds.filter(Boolean))];
  if (ids.length === 0) return {};

  const response = await environmentClient.listEnvironment(
    {
      pageParams: { pageSize: ids.length, pageToken: "" },
      options: {
        includeDeleted: true,
        idFilter: ids,
        fetchEnvironmentOnly: true,
      },
    },
    options,
  );

  return Object.fromEntries(
    response.items.map((environment) => [
      environment.environmentId,
      environment.name || environment.environmentId,
    ]),
  );
}

export async function getEnvironmentForAdminSearch(
  environmentId: string,
  options?: CallOptions,
) {
  const environment = await getAdminEnvironmentProto(environmentId, options);
  return mapAdminSearchEnvironment(environment);
}
